import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { PlusIcon } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useCaseTypes } from "@/hooks/use-case-types";
import { useAdminAccess } from "@/hooks/use-admin-access";
import { queryKeys } from "@/lib/query-keys";
import { CaseTypeHeader } from "@/components/configuration/case-type-header";
import { StepTemplateList } from "@/components/configuration/step-template-list";
import { EventTemplateList } from "@/components/configuration/event-template-list";
import { CaseTypeDialog } from "@/components/configuration/case-type-dialog";
import { CopyStepsFromDialog } from "@/components/configuration/copy-steps-from-dialog";
import { usePathname, useRouter, useSearchParams } from "@/i18n/navigation";
import client from "@/lib/api";
import type { ApiCaseType } from "@/types/api-helpers";

export const Route = createFileRoute("/_dashboard/configuration/case-types")({
  validateSearch: (search: Record<string, unknown>) => ({
    type:
      typeof search.type === "string" ? search.type.slice(0, 100) : undefined,
  }),
  component: CaseTypesPage,
});

function CaseTypesPage() {
  const t = useTranslations("CaseTypesPage");
  const queryClient = useQueryClient();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const rawSearch = searchParams.toString();
  const requestedTypeId = (searchParams.get("type") ?? "").slice(0, 100);
  const {
    isAdmin,
    isLoading: isCheckingAccess,
    isError: accessError,
    refetch: refetchAccess,
  } = useAdminAccess();
  const {
    caseTypes,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useCaseTypes({ enabled: isAdmin, suppressGlobalError: true });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ApiCaseType | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [copyFromOpen, setCopyFromOpen] = useState(false);

  const selectedCaseType = useMemo(
    () =>
      caseTypes.find((caseType) => caseType.id === requestedTypeId) ??
      caseTypes[0],
    [caseTypes, requestedTypeId]
  );
  const selectedId = selectedCaseType?.id;

  useEffect(() => {
    if (
      pathname !== "/configuration/case-types" ||
      !isAdmin ||
      isLoading ||
      isError
    ) {
      return;
    }

    const expected = new URLSearchParams();
    if (selectedId) expected.set("type", selectedId);
    if (rawSearch !== expected.toString()) {
      router.replace(
        selectedId
          ? `/configuration/case-types?${expected.toString()}`
          : "/configuration/case-types"
      );
    }
  }, [
    isAdmin,
    isError,
    isLoading,
    pathname,
    rawSearch,
    router,
    selectedId,
  ]);

  function selectCaseType(id?: string, replace = false) {
    const url = id
      ? `/configuration/case-types?type=${encodeURIComponent(id)}`
      : "/configuration/case-types";
    if (replace) router.replace(url);
    else router.push(url);
  }

  function openCreate() {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(caseType: ApiCaseType) {
    setEditing(caseType);
    setDialogOpen(true);
  }

  async function handleSaveCaseType(data: {
    name: string;
    description: string | null;
  }) {
    setIsSaving(true);
    try {
      let createdId: string | undefined;
      if (editing) {
        await client.PATCH("/admin/case-types/{id}", {
          params: { path: { id: editing.id } },
          body: {
            name: { set: true, valid: true, value: data.name },
            description:
              data.description !== null
                ? { set: true, valid: true, value: data.description }
                : { set: true, valid: false },
          },
        });
      } else {
        const { data: created } = await client.POST("/admin/case-types", {
          body: data,
        });
        if (!created) throw new Error("Missing created case type");
        createdId = created.id;
      }

      await queryClient.invalidateQueries({ queryKey: queryKeys.caseTypes });
      if (createdId) selectCaseType(createdId, true);
      toast.success(
        editing ? t("caseTypeUpdateSuccess") : t("caseTypeCreateSuccess")
      );
      setDialogOpen(false);
      setEditing(null);
    } catch {
      toast.error(t("caseTypeSaveError"));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeleteCaseType(caseType: ApiCaseType) {
    const remaining = caseTypes.filter((item) => item.id !== caseType.id);
    try {
      await client.DELETE("/admin/case-types/{id}", {
        params: { path: { id: caseType.id } },
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.caseTypes });
      selectCaseType(remaining[0]?.id, true);
      toast.success(t("caseTypeDeleteSuccess"));
      return true;
    } catch {
      toast.error(t("caseTypeDeleteError"));
      return false;
    }
  }

  return (
    <>
      <PageHeader title={t("title")} />
      <div className="flex flex-1 flex-col gap-4 p-4">
        <h1 className="sr-only">{t("title")}</h1>

        {isCheckingAccess ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : accessError ? (
          <div
            role="alert"
            className="flex min-h-40 flex-col items-center justify-center gap-3 rounded-md border p-6 text-center"
          >
            <p className="text-muted-foreground text-sm">
              {t("accessCheckError")}
            </p>
            <Button variant="outline" onClick={() => void refetchAccess()}>
              {t("retry")}
            </Button>
          </div>
        ) : !isAdmin ? (
          <div role="alert" className="rounded-md border p-4 text-sm">
            {t("adminOnly")}
          </div>
        ) : (
          <>
            <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
              <p className="text-muted-foreground text-sm">
                {t("description")}
              </p>
              <Button variant="outline" size="sm" onClick={openCreate}>
                <PlusIcon className="size-4" />
                {t("addCaseType")}
              </Button>
            </div>

            {isError ? (
              <div
                role="alert"
                className="flex min-h-40 flex-col items-center justify-center gap-3 rounded-md border p-6 text-center"
              >
                <p className="text-muted-foreground text-sm">
                  {t("loadError")}
                </p>
                <Button
                  variant="outline"
                  onClick={() => void refetch()}
                  disabled={isFetching}
                >
                  {isFetching ? t("retrying") : t("retry")}
                </Button>
              </div>
            ) : isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-48 w-full" />
              </div>
            ) : caseTypes.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {t("noCaseTypes")}
              </p>
            ) : (
              <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1fr)_16rem]">
                <div className="min-w-0 space-y-6 lg:col-start-1 lg:row-start-1">
                  {selectedCaseType && (
                    <>
                      <CaseTypeHeader
                        caseType={selectedCaseType}
                        onEdit={() => openEdit(selectedCaseType)}
                        onDelete={() =>
                          handleDeleteCaseType(selectedCaseType)
                        }
                        onCopyStepsFrom={() => setCopyFromOpen(true)}
                      />
                      <div>
                        <h2 className="mb-3 text-sm font-medium">
                          {t("eventTemplates")}
                        </h2>
                        <EventTemplateList
                          scope="case"
                          caseTypeId={selectedCaseType.id}
                        />
                      </div>
                      <div>
                        <h2 className="mb-3 text-sm font-medium">
                          {t("steps")}
                        </h2>
                        <StepTemplateList caseTypeId={selectedCaseType.id} />
                      </div>
                    </>
                  )}
                </div>
                <nav
                  aria-label={t("caseTypeNavigation")}
                  className="order-first w-full overflow-x-auto rounded-xl border bg-card p-2 lg:order-last lg:col-start-2 lg:row-start-1 lg:sticky lg:top-4 lg:max-h-[calc(100vh-8rem)] lg:self-start lg:overflow-y-auto"
                >
                  <div className="flex gap-1 lg:flex-col">
                    {caseTypes.map((caseType) => (
                      <button
                        key={caseType.id}
                        type="button"
                        aria-pressed={caseType.id === selectedId}
                        onClick={() => selectCaseType(caseType.id)}
                        className={cn(
                          "w-auto shrink-0 whitespace-nowrap rounded-md px-3 py-2 text-left text-sm transition-colors lg:w-full",
                          caseType.id === selectedId
                            ? "bg-accent font-medium text-accent-foreground"
                            : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                        )}
                      >
                        {caseType.name}
                      </button>
                    ))}
                  </div>
                </nav>
              </div>
            )}

            <CaseTypeDialog
              open={dialogOpen}
              onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) setEditing(null);
              }}
              caseType={editing}
              onSave={handleSaveCaseType}
              isSubmitting={isSaving}
            />

            {selectedCaseType && (
              <CopyStepsFromDialog
                open={copyFromOpen}
                onOpenChange={setCopyFromOpen}
                targetCaseTypeId={selectedCaseType.id}
                targetCaseTypeName={selectedCaseType.name}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}
